import { Box, Button, Stack } from "@mui/material";
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnsPanel,
  GridSortModel,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { DataGridPreferences, DataGrids, User } from "../../context/UserContext";
import { DataGridSortableColumns, SortDirection } from "../../graphql/types";
import { CustomColumnsPanelProps } from "../../styles/sbDesignSystem/sbComponents";
import { IDateRange } from "../../types";
import { IDownloadFilters } from "../DownloadsDataGrid/downloadsDatagridUtils";
import { IUploadsFilter } from "../UploadsDataGrid/uploadsDatagridUtils";

const applyDateRangeFilter = (filterValue: { value?: IDateRange }, rowDateStarted: Date, rowDateFinished: Date) => {
  if (!filterValue.value?.start || !filterValue.value?.end) {
    return true;
  }
  const startInUTC = new Date(format(filterValue.value.start, "yyyy-MM-dd:00:00:00") + "Z");
  const endInUTC = new Date(format(filterValue.value.end, "yyyy-MM-dd:23:59:59") + "Z");
  return (
    rowDateStarted >= startInUTC &&
    rowDateFinished >= startInUTC &&
    rowDateStarted <= endInUTC &&
    rowDateFinished <= endInUTC
  );
};

const getSortBy = (field?: string): DataGridSortableColumns | undefined => {
  switch (field) {
    case "report_datetime":
      return DataGridSortableColumns.ReportDatetime;
    case "created":
      return DataGridSortableColumns.Created;
    case "started":
      return DataGridSortableColumns.Started;
    case "finished":
      return DataGridSortableColumns.Finished;
    default:
      return undefined;
  }
};

const getSortDirection = (direction?: string | null): SortDirection | undefined => {
  switch (direction) {
    case "asc":
      return SortDirection.Asc;
    case "desc":
      return SortDirection.Desc;
    default:
      return undefined;
  }
};

const SORT_BY = {
  [DataGridSortableColumns.ReportDatetime]: "report_datetime",
  [DataGridSortableColumns.Created]: "created",
  [DataGridSortableColumns.Started]: "started",
  [DataGridSortableColumns.Finished]: "finished",
} as const;

const SORT_DIRECTION = {
  [SortDirection.Asc]: "asc",
  [SortDirection.Desc]: "desc",
} as const;

const getInitialSortModel = (filter: IUploadsFilter | IDownloadFilters): GridSortModel => {
  const field = SORT_BY[filter.sortBy as keyof typeof SORT_BY] || "";
  const sort = SORT_DIRECTION[filter.sortDirection as keyof typeof SORT_DIRECTION];
  return [
    {
      field,
      sort,
    },
  ];
};

const CustomColumnsPanel = ({
  resetColumnVisibilityModel,
  ...props
}: React.ComponentProps<typeof GridColumnsPanel> & CustomColumnsPanelProps) => {
  return (
    <Box
      sx={(theme) => ({
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.surfaceLight10.main,
        border: `1px solid ${theme.palette.surfaceLight30.main}`,
        borderRadius: "12px",
      })}
    >
      <GridColumnsPanel {...props} sx={{ minHeight: 0 }} />
      <Stack sx={{ padding: 0.5 }}>
        <Button onClick={resetColumnVisibilityModel}>Reset columns</Button>
      </Stack>
    </Box>
  );
};

const customColumnWidthChange = (
  column: GridColumnResizeParams,
  keyName: DataGrids,
  user: User,
  updateDataGridPreferences: (key: DataGrids, value: DataGridPreferences) => void
) => {
  const field = column.colDef.field;
  const newWidth = column.width;
  updateDataGridPreferences(keyName, {
    columnsWidth: {
      ...user.datagridPreferences[keyName]?.columnsWidth,
      [field]: newWidth,
    },
    columnsOrder: user.datagridPreferences[keyName]?.columnsOrder,
    columnVisibilityModel: user.datagridPreferences[keyName]?.columnVisibilityModel,
  });
};

const customOnColumnOrderChange = (
  column: GridColumnOrderChangeParams,
  keyName: DataGrids,
  user: User,
  updateDataGridPreferences: (key: DataGrids, value: DataGridPreferences) => void,
  startingColumns: GridColDef[]
) => {
  const {
    column: { field },
    oldIndex,
    targetIndex: newIndex,
  } = column;
  const currentOrder = startingColumns.map((c) => c.field);
  const newOrder = [...currentOrder];
  newOrder.splice(oldIndex, 1);
  newOrder.splice(newIndex, 0, field);
  updateDataGridPreferences(keyName, {
    columnsOrder: newOrder,
    columnsWidth: user.datagridPreferences[keyName]?.columnsWidth,
    columnVisibilityModel: user.datagridPreferences[keyName]?.columnVisibilityModel,
  });
};

export {
  applyDateRangeFilter,
  CustomColumnsPanel,
  customColumnWidthChange,
  customOnColumnOrderChange,
  getInitialSortModel,
  getSortBy,
  getSortDirection,
};
